import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, ArrowLeft, Edit2, Trash2, Compass } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import PageTitle from '../components/PageTitle';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import ConfirmDialog from '../components/ConfirmDialog';
import SpellCheckDialog from '../components/SpellCheckDialog';
import { useSpellCheckOnSave } from '../hooks/useSpellCheckOnSave';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: 'now', label: 'Now' },
  { value: 'next', label: 'Next' },
  { value: 'later', label: 'Later' },
  { value: 'considering', label: 'Considering' },
  { value: 'released', label: 'Released' },
];

const CATEGORIES = [
  { value: 'feature', label: 'Feature' },
  { value: 'bug', label: 'Bug fix' },
  { value: 'ux', label: 'UX / Design' },
  { value: 'integration', label: 'Integration' },
  { value: 'platform', label: 'Platform' },
  { value: 'other', label: 'Other' },
];

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public (customers see this)' },
  { value: 'internal', label: 'Internal (staff only)' },
];

const emptyForm = () => ({
  title: '', description: '', status: 'next', category: 'feature',
  eta: '', sort_order: 0, linked_suggestion_id: '', visibility: 'public',
});

const RoadmapAdmin = () => {
  const { user } = useAuth();
  const isStaff = isStaffOrAbove(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/roadmap`, { withCredentials: true });
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to load roadmap');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isStaff) fetchItems(); }, [fetchItems, isStaff]);

  // Hooks for spell-check must run before the auth early-return so React's
  // call order stays stable across renders. The hook itself is a no-op until
  // sc.save() is invoked.
  const buildPayload = useCallback(() => ({
    title: form.title.trim(), description: form.description.trim(),
    status: form.status, category: form.category,
    eta: form.eta.trim() || null, sort_order: parseInt(form.sort_order, 10) || 0,
    linked_suggestion_id: form.linked_suggestion_id.trim() || null,
    visibility: form.visibility || 'public',
  }), [form]);

  const persist = useCallback(async (payload) => {
    try {
      if (editing) {
        await axios.put(`${API}/roadmap/${editing.roadmap_id}`, payload, { withCredentials: true });
        toast.success('Roadmap item updated');
      } else {
        await axios.post(`${API}/roadmap`, payload, { withCredentials: true });
        toast.success('Roadmap item added');
      }
      setDialogOpen(false); setEditing(null); setForm(emptyForm());
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  }, [editing, fetchItems]);

  const sc = useSpellCheckOnSave({ buildPayload, persist });

  if (!isStaff) return <Navigate to="/home" replace />;

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title, description: item.description || '', status: item.status,
      category: item.category, eta: item.eta || '', sort_order: item.sort_order || 0,
      linked_suggestion_id: item.linked_suggestion_id || '',
      visibility: item.visibility || 'public',
    });
    setDialogOpen(true);
  };

  const save = (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) { toast.error('Title must be at least 3 characters'); return; }
    sc.save();
  };

  const confirmDelete = async () => {
    const t = pendingDelete; setPendingDelete(null);
    if (!t) return;
    try {
      await axios.delete(`${API}/roadmap/${t.roadmap_id}`, { withCredentials: true });
      toast.success('Roadmap item removed');
      setItems((prev) => prev.filter((x) => x.roadmap_id !== t.roadmap_id));
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12" data-testid="roadmap-admin-page">
      <Link to="/home" className="inline-flex items-center text-sm text-[#64748B] hover:text-[#020617] mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to home
      </Link>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <PageTitle
          title="Edit roadmap"
          subtitle="Curate what shows up under Upcoming Improvements. No voting — these are decisions."
          className="mb-0"
        />
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="add-roadmap-button">
              <Plus className="w-4 h-4 mr-2" />
              Add item
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl" data-testid="roadmap-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#020617]">{editing ? 'Edit' : 'New'} roadmap item</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label htmlFor="rm-title">Title</Label>
                <Input id="rm-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" data-testid="roadmap-title-input" />
              </div>
              <div>
                <Label htmlFor="rm-desc">Description</Label>
                <Textarea id="rm-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="roadmap-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="rm-eta">ETA (e.g. Q3 2026)</Label>
                  <Input id="rm-eta" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} placeholder="Q3 2026" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="rm-sort">Sort order</Label>
                  <Input id="rm-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Visibility</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="roadmap-visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VISIBILITY_OPTIONS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-xs text-[#64748B]">
                  Internal items (e.g. VAT setup, security audits, infra refactors) stay hidden from customers — only staff and admin see them on the home page roadmap.
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="roadmap-save-button">{editing ? 'Save changes' : 'Add item'}</Button>
                <Button type="button" onClick={() => setDialogOpen(false)} className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052FF]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
          <p className="text-[#64748B]">No roadmap items yet. Click "Add item" to publish your first.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.roadmap_id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-start justify-between gap-3" data-testid={`roadmap-row-${item.roadmap_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{item.status}</span>
                  <span className="text-xs text-[#64748B]">{item.category}</span>
                  {item.eta && <span className="text-xs text-[#64748B]">· {item.eta}</span>}
                  {item.visibility === 'internal' && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]"
                      title="Hidden from customers — staff only"
                      data-testid={`roadmap-internal-pill-${item.roadmap_id}`}
                    >
                      Internal
                    </span>
                  )}
                </div>
                <h3 className="text-base font-semibold text-[#020617]">{item.title}</h3>
                {item.description && <p className="text-sm text-[#64748B] mt-1 whitespace-pre-line">{item.description}</p>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(item)} className="p-2 rounded-md hover:bg-[#F1F5F9] text-[#475569]" aria-label="Edit"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => setPendingDelete(item)} className="p-2 rounded-md hover:bg-[#FEE2E2] text-[#EF4444]" aria-label="Delete" data-testid={`roadmap-delete-${item.roadmap_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        title="Delete this roadmap item?"
        description={pendingDelete ? `"${pendingDelete.title}" will be permanently removed.` : 'This cannot be undone.'}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />
      <SpellCheckDialog {...sc.dialogProps} />
    </div>
  );
};

export default RoadmapAdmin;
